import _ from 'lodash';

export interface PaymentItemKeyInput {
    controlNumber?: string;
    procedureCode?: string;
    modifierCodes?: string[];
    fromDos?: string;
}

export interface ProfessionalServiceLineKeyInput {
    x12ControlNumber?: string;
    f24dProcedureCode?: string;
    f24dProcedureModifiers?: string[];
    f24aFromDateOfService?: string;
}

export interface InstitutionalServiceLineKeyInput {
    x12ControlNumber?: string;
    f44ProcedureCode?: string;
    f44ModifierCodes?: string[];
    f45FromServiceDate?: string;
}

export function getPaymentItemFallbackKeys(item: PaymentItemKeyInput): string[] {
    const procedureCode = item.procedureCode ? _.toUpper(_.trim(item.procedureCode)) : undefined;
    const fromDos = item.fromDos ? _.trim(item.fromDos) : undefined;
    const normalizedModifiers = _.compact(
        (item.modifierCodes || []).map(modifier => {
            const trimmed = modifier ? _.trim(modifier) : modifier;
            return trimmed ? _.toUpper(trimmed) : undefined;
        })
    );

    normalizedModifiers.sort();
    const checkModifiers = _.join(normalizedModifiers, '');

    const keys: string[] = [];
    if (procedureCode && fromDos) {
        if (checkModifiers) {
            keys.push(_.compact([procedureCode, checkModifiers, fromDos]).join('-'));
        } else {
            keys.push(_.compact([procedureCode, fromDos]).join('-'));
        }
    }

    return _.uniq(keys);
}

export function getPaymentItemKey(
    item: PaymentItemKeyInput,
    existingControlNumbers?: (string | undefined)[]
): string | undefined {
    if (item.controlNumber) {
        const itemControlNumberTrimmed = _.trim(item.controlNumber);
        if (itemControlNumberTrimmed) {
            const itemControlNumberUpper = _.toUpper(itemControlNumberTrimmed);
            const matchesExisting =
                !existingControlNumbers ||
                existingControlNumbers.some(controlNumber => {
                    if (!controlNumber) {
                        return false;
                    }

                    const trimmed = _.trim(controlNumber);
                    return !!trimmed && _.toUpper(trimmed) === itemControlNumberUpper;
                });

            if (matchesExisting) {
                return itemControlNumberUpper;
            }
        }
    }

    return _.first(getPaymentItemFallbackKeys(item));
}

export function getProfessionalServiceLineFallbackKeys(line: ProfessionalServiceLineKeyInput): string[] {
    const procedureCode = line.f24dProcedureCode ? _.toUpper(_.trim(line.f24dProcedureCode)) : undefined;
    const fromDos = line.f24aFromDateOfService ? _.trim(line.f24aFromDateOfService) : undefined;
    const normalizedModifiers = _.compact(
        (line.f24dProcedureModifiers || []).map(modifier => {
            const trimmed = modifier ? _.trim(modifier) : modifier;
            return trimmed ? _.toUpper(trimmed) : undefined;
        })
    );

    normalizedModifiers.sort();
    const checkModifiers = _.join(normalizedModifiers, '');

    const keys: string[] = [];
    if (procedureCode && fromDos) {
        if (checkModifiers) {
            keys.push(_.compact([procedureCode, checkModifiers, fromDos]).join('-'));
        } else {
            keys.push(_.compact([procedureCode, fromDos]).join('-'));
        }
    }

    return _.uniq(keys);
}

export function getProfessionalServiceLineKey(line: ProfessionalServiceLineKeyInput): string | undefined {
    if (line.x12ControlNumber) {
        const trimmed = _.trim(line.x12ControlNumber);
        if (trimmed) {
            return _.toUpper(trimmed);
        }
    }

    return _.first(getProfessionalServiceLineFallbackKeys(line));
}

export function getInstitutionalServiceLineKey(line: InstitutionalServiceLineKeyInput): string | undefined {
    if (line.x12ControlNumber) {
        const trimmed = _.trim(line.x12ControlNumber);
        if (trimmed) {
            return _.toUpper(trimmed);
        }
    }

    const procedureCode = line.f44ProcedureCode ? _.toUpper(_.trim(line.f44ProcedureCode)) : undefined;
    const fromDos = line.f45FromServiceDate ? _.trim(line.f45FromServiceDate) : undefined;
    const normalizedModifiers = _.compact(
        (line.f44ModifierCodes || []).map(modifier => {
            const trimmed = modifier ? _.trim(modifier) : modifier;
            return trimmed ? _.toUpper(trimmed) : undefined;
        })
    );

    normalizedModifiers.sort();
    const checkModifiers = _.join(normalizedModifiers, '');

    if (procedureCode && fromDos) {
        if (checkModifiers) {
            return _.compact([procedureCode, checkModifiers, fromDos]).join('-');
        }

        return _.compact([procedureCode, fromDos]).join('-');
    }

    return;
}
